import dayjs from "dayjs";
import { renderToStaticMarkup } from "react-dom/server";
import { steelWeight } from "@/pages/ItemEditor/steelWeight";
import { useTranslation } from "react-i18next";

export type PrintProduct = {
  id?: string;
  name?: string;
  amount?: number | string | null;
  unitPrice?: number | string | null;
  unitWeight?: number | string | null;
  length?: number | string | null;
};

export type PurchaseOrderPrintData = {
  companyName?: string | null;
  companySubName?: string | null;
  name?: string | null;
  purchaseDate?: string | Date | null;
  deliveryDate?: string | Date | null;
  partnerName?: string | null;
  partnerContact?: string | null;
  partnerTelephone?: string | null;
  partnerFax?: string | null;
  paymentMethod?: string | null;
  products?: PrintProduct[];
  remark?: string | null;
  notice?: string | null;
  applicantName?: string | null;
  reviewerName?: string | null;
};

export const DEFAULT_NOTICE_LINES = [
  "1.供方收到此訂單後，請簽字或蓋章回傳，如對此訂單內容有異議，請速聯繫。",
  "2.請依照訂單上之指定日期及數量交貨，若未經本公司同意，而延期交或者，",
  "　本公司有權要求賠償或進行取消訂單。",
  "3.貨品雖經買方驗收後，因品質不良導致退貨時，不能免除供合格產品之責任，",
  "　並由賣方負責退回所有不合格品，退費由賣方負擔，本公司有權要求賠償。",
  "4.每月25日為關帳日，26日以後出貨列為隔月帳款。",
];

const MIN_PRODUCT_ROWS = 6;

// 範例檔用的是 SimSun（宋體），活頁簿預設字型為新細明體，都是明體系；
// macOS 沒有這兩套時退到系統內建的宋體
const PRINT_FONT_FAMILY =
  '"SimSun", "宋體", "新細明體", PMingLiU, "Songti TC", "Songti SC", serif';

const toNumber = (value: unknown): number | null => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export function printRowWeight(product: PrintProduct): number | null {
  const perPiece = steelWeight(
    product?.unitWeight ?? null,
    product?.length ?? null,
  );
  const amount = toNumber(product?.amount);
  if (perPiece == null || amount == null) return null;
  return Math.round(perPiece * amount * 10) / 10;
}

export function printRowSubtotal(product: PrintProduct): number | null {
  const unitPrice = toNumber(product?.unitPrice);
  const amount = toNumber(product?.amount);
  if (unitPrice == null || amount == null) return null;
  return Math.round(unitPrice * amount * 100) / 100;
}

export function printTotals(products: PrintProduct[] = []) {
  let amount: number | null = null;
  let weight: number | null = null;
  let subtotal: number | null = null;

  for (const product of products) {
    const rowAmount = toNumber(product?.amount);
    if (rowAmount != null) amount = (amount ?? 0) + rowAmount;

    const rowWeight = printRowWeight(product);
    if (rowWeight != null) weight = (weight ?? 0) + rowWeight;

    const rowSubtotal = printRowSubtotal(product);
    if (rowSubtotal != null) subtotal = (subtotal ?? 0) + rowSubtotal;
  }

  return {
    amount,
    weight: weight == null ? null : Math.round(weight * 10) / 10,
    subtotal: subtotal == null ? null : Math.round(subtotal),
  };
}

const formatNumber = (value: number | null) =>
  value == null
    ? ""
    : value.toLocaleString("zh-TW", { maximumFractionDigits: 2 });

const formatDate = (value: string | Date | null | undefined) =>
  value ? dayjs(value).format("YYYY-MM-DD") : "";

function PurchaseOrderPrintSheet({ data }: { data: PurchaseOrderPrintData }) {
  const { t } = useTranslation();

  const products = data?.products ?? [];
  const totals = printTotals(products);
  const noticeLines = data?.notice
    ? data.notice.split("\n")
    : DEFAULT_NOTICE_LINES;

  const paddedRows = [
    ...products,
    ...Array.from(
      { length: Math.max(0, MIN_PRODUCT_ROWS - products.length) },
      () => null,
    ),
  ];

  const cell = "border border-black px-1.5 py-1 align-top";
  const labelCell = `${cell} whitespace-nowrap`;

  return (
    <div
      className="purchase-order-print-sheet hidden text-black"
      style={{ fontFamily: PRINT_FONT_FAMILY }}
    >
      <table className="w-full table-fixed border-collapse text-[14pt] leading-tight">
        <colgroup>
          <col style={{ width: "15.6%" }} />
          <col style={{ width: "30.5%" }} />
          <col style={{ width: "13.5%" }} />
          <col style={{ width: "13.5%" }} />
          <col style={{ width: "13.5%" }} />
          <col style={{ width: "13.4%" }} />
        </colgroup>
        <tbody>
          <tr>
            <td
              className={`${cell} text-center text-[18pt] font-bold`}
              colSpan={6}
            >
              {data?.companyName || ""}
            </td>
          </tr>
          {/* <tr>
            <td className={`${cell} text-center text-[18pt] font-bold`} colSpan={6}>
              {data?.companySubName || ""}
            </td>
          </tr> */}
          <tr>
            <td
              className={`${cell} text-center text-[16pt] font-bold tracking-[0.3em]`}
              colSpan={6}
            >
              {t("purchaseOrder.printTitle") || "採購單"}
            </td>
          </tr>
          <tr>
            <td className={labelCell}>
              {t("purchaseOrder.purchaseOrderName") || "採購單名稱"}:
            </td>
            <td className={cell} colSpan={5}>
              {data?.name || ""}
            </td>
          </tr>
          <tr>
            <td className={labelCell}>
              {t("purchaseOrder.purchaseDate") || "採購日期"}:
            </td>
            <td className={cell}>{formatDate(data?.purchaseDate)}</td>
            <td className={labelCell}>
              {t("purchaseOrder.deliveryDate") || "交貨日期"}:
            </td>
            <td className={cell} colSpan={3}>
              {formatDate(data?.deliveryDate)}
            </td>
          </tr>
          <tr>
            <td className={labelCell}>
              {t("purchaseOrder.partner") || "合作方"}:
            </td>
            <td className={cell}>{data?.partnerName || ""}</td>
            <td className={labelCell}>
              {t("purchaseOrder.printContact") || "聯絡人"}:
            </td>
            <td className={cell} colSpan={3}>
              {data?.partnerContact || ""}
            </td>
          </tr>
          <tr>
            <td className={labelCell}>
              {t("purchaseOrder.printTelephone") || "電話"}:
            </td>
            <td className={cell}>{data?.partnerTelephone || ""}</td>
            <td className={labelCell}>
              {t("purchaseOrder.printFax") || "傳真"}:
            </td>
            <td className={cell} colSpan={3}>
              {data?.partnerFax || ""}
            </td>
          </tr>
          <tr>
            <td className={labelCell}>
              {t("purchaseOrder.paymentMethod") || "付款方式"}:
            </td>
            <td className={cell} colSpan={5}>
              {data?.paymentMethod || ""}
            </td>
          </tr>
          <tr>
            <td className={`${cell} text-center font-bold`}>
              {t("purchaseOrder.printItemNo") || "項次"}
            </td>
            <td className={`${cell} text-center font-bold`}>
              {t("purchaseOrder.productName") || "商品名稱"}
            </td>
            <td className={`${cell} text-center font-bold`}>
              {t("purchaseOrder.quantity") || "數量"}
            </td>
            <td className={`${cell} text-center font-bold`}>
              {t("purchaseOrder.printWeight") || "重量(kg)"}
            </td>
            <td className={`${cell} text-center font-bold`}>
              {t("purchaseOrder.unitPrice") || "單價"}
            </td>
            <td className={`${cell} text-center font-bold`}>
              {t("purchaseOrder.subtotal") || "小計"}
            </td>
          </tr>
          {paddedRows.map((product, index) => (
            <tr key={product?.id || `blank-${index}`}>
              <td className={`${cell} text-center`}>{index + 1}</td>
              <td className={cell}>{product?.name || ""}</td>
              <td className={`${cell} text-right`}>
                {product ? formatNumber(toNumber(product.amount)) : ""}
              </td>
              <td className={`${cell} text-right`}>
                {product ? formatNumber(printRowWeight(product)) : ""}
              </td>
              <td className={`${cell} text-right`}>
                {product ? formatNumber(toNumber(product.unitPrice)) : ""}
              </td>
              <td className={`${cell} text-right`}>
                {product ? formatNumber(printRowSubtotal(product)) : ""}
              </td>
            </tr>
          ))}
          <tr>
            <td className={cell} />
            <td className={cell} />
            <td className={`${cell} text-center`}>
              {t("purchaseOrder.printAmountSubtotal") || "數量小計"}
            </td>
            <td className={`${cell} text-right`}>
              {formatNumber(totals.amount)}
            </td>
            <td className={`${cell} text-center`}>
              {t("purchaseOrder.printWeightSubtotal") || "重量小計"}
            </td>
            <td className={`${cell} text-right`}>
              {formatNumber(totals.weight)}
            </td>
          </tr>
          <tr>
            <td className={cell} />
            <td className={cell} />
            <td className={cell} />
            <td className={cell} />
            <td className={`${cell} text-center`}>
              {t("purchaseOrder.printGrandTotal") || "金額總計"}
            </td>
            <td className={`${cell} text-right`}>
              {formatNumber(totals.subtotal)}
            </td>
          </tr>
          <tr>
            <td className={`${cell} text-center`}>
              {t("purchaseOrder.remark") || "備註"}
            </td>
            <td className={`${cell} h-16 whitespace-pre-wrap`} colSpan={5}>
              {data?.remark || ""}
            </td>
          </tr>
          <tr>
            <td className={`${cell} text-[12pt] font-bold`} colSpan={6}>
              {t("purchaseOrder.printNoticeTitle") || "【注意事項】"}
            </td>
          </tr>
          {noticeLines.map((line, index) => (
            <tr key={`notice-${index}`}>
              <td
                className={`${cell} whitespace-pre-wrap text-[12pt]`}
                colSpan={6}
              >
                {line}
              </td>
            </tr>
          ))}
          <tr>
            <td className={`${cell} py-4`} colSpan={6}>
              <span className="mr-16">
                {t("purchaseOrder.printFinance") || "財務"}:
              </span>
              <span className="mr-16">
                {t("purchaseOrder.printApprover") || "審核"}:
                {data?.reviewerName || ""}
              </span>
              <span>
                {t("purchaseOrder.printApplicant") || "申請人"}:
                {data?.applicantName || ""}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function purchaseOrderPrintHtml(
  data: PurchaseOrderPrintData,
  title: string,
): string {
  const sheet = renderToStaticMarkup(<PurchaseOrderPrintSheet data={data} />);
  const styles = Array.from(
    document.head.querySelectorAll('style, link[rel="stylesheet"]'),
  )
    .map((node) => node.outerHTML)
    .join("");
  const escapedTitle = title
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<!doctype html><html><head><meta charset="utf-8" /><title>${escapedTitle}</title>${styles}<style>
@page { size: A4 portrait; margin: 0; }
html, body { margin: 0; background: #fff; }
.purchase-order-print-sheet { display: block !important; padding: 10mm; }
</style></head><body>${sheet}</body></html>`;
}

export default PurchaseOrderPrintSheet;
